/**
 * <product-card-media>
 *
 * Turns the media area of a product card into a Swiper carousel.
 *
 * A card renders one carousel per unique variant image; only the one matching
 * the selected variant is visible, the rest carry `.hidden` and are swapped in
 * by ProductCard.updateMediaForVariant. Sliders are therefore built lazily —
 * a collection page with many colourways would otherwise spin up hundreds of
 * Swiper instances that nobody ever sees.
 *
 * Desktop: arrows fade in on card hover, and hovering advances to the second
 * slide (the old hover-image behaviour). Mobile: swipe, with dot indicators
 * along the bottom edge.
 */
if (!customElements.get("product-card-media")) {
  const CAROUSEL_SELECTOR = ".product-card__media-carousel";
  const SLIDE_SELECTOR = ".swiper-slide";

  const desktopHover = window.matchMedia(
    "(min-width: 750px) and (hover: hover)"
  );

  // Shared across every card — one observer beats one per card on a long grid.
  const viewportObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        entry.target.activate();
      });
    },
    { rootMargin: "300px 0px" }
  );

  class ProductCardMedia extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.carousels = Array.from(this.querySelectorAll(CAROUSEL_SELECTOR));
      if (!this.carousels.length) return;

      this.initialized = true;
      this.sliders = new Map();
      this.isInViewport = false;
      this.hoverAdvance = this.dataset.hoverAdvance === "true";

      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);

      // Variant swatches toggle `.hidden` on the carousel matching the chosen
      // variant. Watch for that so the incoming carousel gets built / resized —
      // Swiper cannot measure a `display: none` element. Only an actual change
      // in visibility counts: Swiper writes its own classes onto the same
      // element, and reacting to those would reset a slide mid-transition.
      this.hiddenState = new WeakMap();
      this.classObserver = new MutationObserver(mutations => {
        const visibilityChanged = mutations.some(mutation => {
          const carousel = mutation.target;
          const isHidden = carousel.classList.contains("hidden");
          if (this.hiddenState.get(carousel) === isHidden) return false;
          this.hiddenState.set(carousel, isHidden);
          return true;
        });
        if (visibilityChanged) this.onVisibleCarouselChange();
      });
      this.carousels.forEach(carousel => {
        this.hiddenState.set(carousel, carousel.classList.contains("hidden"));
        this.classObserver.observe(carousel, {
          attributes: true,
          attributeFilter: ["class"]
        });
      });

      viewportObserver.observe(this);

      if (this.hoverAdvance) {
        // Bind on the actions wrapper, not on the media itself: the wishlist and
        // add-to-cart buttons sit on top of the media as siblings, so hovering
        // them would otherwise read as a mouseleave.
        this.hoverTarget = this.closest(".product-card__actions") || this;
        this.hoverTarget.addEventListener("mouseenter", this.onMouseEnter);
        this.hoverTarget.addEventListener("mouseleave", this.onMouseLeave);
      }
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      viewportObserver.unobserve(this);
      this.classObserver?.disconnect();

      if (this.hoverTarget) {
        this.hoverTarget.removeEventListener("mouseenter", this.onMouseEnter);
        this.hoverTarget.removeEventListener("mouseleave", this.onMouseLeave);
      }

      this.sliders.forEach(slider => slider.destroy(true, true));
      this.sliders.clear();
      this.initialized = false;
    }

    get visibleCarousel() {
      return (
        this.carousels.find(
          carousel => !carousel.classList.contains("hidden")
        ) || null
      );
    }

    get visibleSlider() {
      const carousel = this.visibleCarousel;
      return carousel ? this.sliders.get(carousel) : null;
    }

    /** Called by the shared IntersectionObserver once the card nears the viewport. */
    activate() {
      this.isInViewport = true;
      this.mountVisibleCarousel();
      this.warmHiddenCarousels();
    }

    onVisibleCarouselChange() {
      // The incoming carousel can still be lazy — a swatch used before the idle
      // warm-up got a slot. Ask for it now, and at high priority: it is the
      // image the shopper is waiting on.
      this.fetchNow(this.visibleCarousel?.querySelector("img"), "high");

      if (!this.isInViewport) return;
      this.mountVisibleCarousel();
    }

    /**
     * A `loading="lazy"` image inside a `display: none` carousel is never
     * fetched — it has no box, so it never intersects anything. The first
     * swatch pick therefore paid for a full download after the click, which is
     * what read as the image loading slowly. Start those first frames as soon
     * as the card nears the viewport, at low priority so they queue behind the
     * images actually on screen; by the time a swatch is used the swap is a
     * repaint rather than a request.
     */
    warmHiddenCarousels() {
      const warm = () => {
        this.carousels.forEach(carousel => {
          if (!carousel.classList.contains("hidden")) return;
          this.fetchNow(carousel.querySelector("img"), "low");
        });
      };

      if ("requestIdleCallback" in window) {
        requestIdleCallback(warm, { timeout: 1000 });
      } else {
        warm();
      }
    }

    /** Take an image off lazy loading so the browser requests it right away. */
    fetchNow(image, priority) {
      if (!image || image.dataset.eager) return;
      image.dataset.eager = "true";
      if (priority) image.setAttribute("fetchpriority", priority);
      image.loading = "eager";
    }

    mountVisibleCarousel() {
      const carousel = this.visibleCarousel;
      if (!carousel) return;

      const existing = this.sliders.get(carousel);
      if (existing) {
        // Coming back from `display: none` — remeasure and start from the top.
        // If the carousel was hidden mid-fade (desktop: a swatch clicked while
        // the hover transition was still running) the slides' transitions were
        // cancelled without firing `transitionend`, so Swiper never cleared
        // `animating`. Left set, it stops every later transition from being
        // registered — and with it the `transitionEnd` that repaints the fade,
        // stranding a slide part-faded. Clear it before remeasuring.
        existing.animating = false;
        existing.update();
        existing.slideTo(0, 0);
        delete carousel.dataset.interacted;
        return;
      }

      if (carousel.querySelectorAll(SLIDE_SELECTOR).length < 2) return;

      if (typeof Swiper === "undefined") {
        window.addEventListener("load", () => this.mountVisibleCarousel(), {
          once: true
        });
        return;
      }

      this.sliders.set(carousel, this.buildSlider(carousel));
    }

    buildSlider(carousel) {
      const prevEl = carousel.querySelector("[data-card-media-prev]");
      const nextEl = carousel.querySelector("[data-card-media-next]");
      const dotsEl = carousel.querySelector("[data-card-media-dots]");

      // No controls in the markup means the media slider is switched off in
      // theme settings: the slides stay stacked purely so hover can crossfade
      // between them, and nothing should navigate away from the first one.
      const hasControls = Boolean(prevEl && nextEl);

      const slider = new Swiper(carousel, {
        a11y: false,
        speed: 500,
        slidesPerView: 1,
        threshold: 6,
        rewind: true,
        watchOverflow: true,
        allowTouchMove: hasControls,
        effect: "fade",
        fadeEffect: { crossFade: true },
        // Slides are links; without these a swipe would also follow the href.
        preventClicks: true,
        preventClicksPropagation: true,
        navigation: prevEl && nextEl ? { prevEl: prevEl, nextEl: nextEl } : false,
        pagination: dotsEl
          ? {
              el: dotsEl,
              type: "bullets",
              bulletClass: "product-card__media-dot",
              bulletActiveClass: "product-card__media-dot--active"
            }
          : false
      });

      // With `effect: fade` a slide's opacity is written from its cached
      // `progress`, not from the active index — and that cache can be left
      // mid-way (opacity 0.5, both images showing through) by a fade that gets
      // cut short: hover pulling away, an arrow click, or a variant swap.
      // Swiper does not always heal it, because its own setTranslate skips
      // recomputing progress whenever the slider's overall progress happens to
      // be unchanged — exactly the case when a hover fade is reversed back to
      // the slide it started on. So recompute progress explicitly, then repaint
      // by re-issuing the active slide at zero speed.
      //
      // The inline opacity of the active slide is Swiper's *target* value, not
      // the animating one, so checking it here is safe mid-fade: a healthy
      // transition always targets 1 and this is a no-op.
      const normalizeFade = () => {
        if (slider.destroyed) return;

        const active = slider.slides[slider.activeIndex];
        if (!active || parseFloat(active.style.opacity) === 1) return;

        slider.updateProgress();
        slider.slideTo(slider.activeIndex, 0, false);
      };
      slider.on("transitionEnd", normalizeFade);
      // After a drag, wait a frame so Swiper's own settle has been applied.
      slider.on("touchEnd", () => requestAnimationFrame(normalizeFade));

      // Slides are images inside links, so on desktop pressing one and moving
      // starts the browser's native drag-and-drop. That fires `pointercancel`,
      // which Swiper deliberately ignores outside Safari — its onTouchEnd
      // returns early for that event type — so the drag is never finished. The
      // slider stays parked between two slides, which is what paints two images
      // at opacity 0.5, and it also leaves `allowClick` false, so the card's
      // link stops responding. Touch drags never trigger native DnD, which is
      // why only desktop is affected.
      //
      // Suppress the native drag, and finish the gesture ourselves if one still
      // gets through.
      carousel.addEventListener("dragstart", event => event.preventDefault());
      carousel.addEventListener("pointercancel", () => {
        if (slider.destroyed) return;

        if (slider.touchEventsData) {
          slider.touchEventsData.isTouched = false;
          slider.touchEventsData.isMoved = false;
        }
        slider.allowClick = true;
        slider.slideToClosest();
      });

      // Once the shopper navigates deliberately, stop hover from hijacking the
      // slide they chose (and from snapping back on mouseleave).
      const markInteracted = () => {
        carousel.dataset.interacted = "true";
      };
      prevEl?.addEventListener("click", markInteracted);
      nextEl?.addEventListener("click", markInteracted);
      // `sliderMove` only fires on an actual drag, unlike `touchStart`.
      slider.on("sliderMove", markInteracted);

      // Mobile gets no arrows, so tapping the left / right half of the media
      // steps through the slides instead of opening the product. Registered
      // after Swiper's own capture-phase handler, which swallows the click that
      // ends a swipe — so a drag never also counts as a tap.
      carousel.addEventListener(
        "click",
        event => {
          if (desktopHover.matches || !hasControls) return;

          const rect = carousel.getBoundingClientRect();
          if (!rect.width) return;

          event.preventDefault();
          event.stopPropagation();
          markInteracted();

          const tappedLeftHalf = event.clientX - rect.left < rect.width / 2;
          const goPrev = slider.rtlTranslate ? !tappedLeftHalf : tappedLeftHalf;

          if (goPrev) {
            slider.slidePrev();
          } else {
            slider.slideNext();
          }
        },
        true
      );

      return slider;
    }

    onMouseEnter() {
      const slider = this.hoverSlider();
      if (!slider || slider.slides.length < 2) return;
      slider.slideTo(1);
    }

    onMouseLeave() {
      const slider = this.hoverSlider();
      if (!slider) return;
      slider.slideTo(0);
    }

    /** The slider hover is allowed to drive, if any. */
    hoverSlider() {
      if (!desktopHover.matches) return null;

      const carousel = this.visibleCarousel;
      if (!carousel || carousel.dataset.interacted === "true") return null;

      return this.sliders.get(carousel) || null;
    }
  }

  customElements.define("product-card-media", ProductCardMedia);
}
